import { useCallback, useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

import { useGruposAnuales } from '#components/mobile/anual/useAnual';
import { useDatosObjetivos } from '#components/mobile/budget/useDatosObjetivos';
import { leerTravesia } from '#components/mobile/travesia/estado';
import { useCategories } from '#hooks/useCategories';
import { useQuery } from '#hooks/useQuery';
import { useSyncedPref } from '#hooks/useSyncedPref';

import {
  calcularHoy,
  categoriasPorDefecto,
  leerCategoriasHoy,
} from './hoyPuedes';
import type { GastoReciente, Hoy } from './hoyPuedes';

/** Días hacia atrás que miran «Repetir» y las categorías más usadas. */
const DIAS_RECIENTES = 60;

function tiposPlantilla(c: CategoryEntity): string[] {
  if (!c.goal_def) {
    return [];
  }
  try {
    const v: unknown = JSON.parse(c.goal_def);
    return Array.isArray(v)
      ? v
          .map(p =>
            p && typeof p === 'object' ? (p as { type?: unknown }).type : null,
          )
          .filter((t): t is string => typeof t === 'string')
      : [];
  } catch {
    return [];
  }
}

/**
 * Datos del widget «Hoy»: qué categorías cuentan (las elegidas o las de por
 * defecto), lo que queda en ellas, lo gastado hoy y el cálculo de
 * `calcularHoy`. `null` en `hoy` mientras llegan los saldos.
 */
export function useHoy(month: string): {
  hoy: Hoy | null;
  ids: string[];
  elegidas: boolean;
  guardarIds: (ids: string[]) => void;
  grupos: readonly CategoryGroupEntity[];
  categorias: readonly CategoryEntity[];
} {
  const {
    data: { list: categorias, grouped: grupos } = {
      list: [] as CategoryEntity[],
      grouped: [] as CategoryGroupEntity[],
    },
  } = useCategories();
  const [raw, setRaw] = useSyncedPref('hoy-categorias');
  const [rawTravesia] = useSyncedPref('travesia');
  const { grupos: anuales } = useGruposAnuales();

  const guardadas = useMemo(() => leerCategoriasHoy(raw), [raw]);
  const ids = useMemo(() => {
    const existentes = new Set(categorias.map(c => c.id));
    if (guardadas) {
      return guardadas.filter(id => existentes.has(id));
    }
    return categoriasPorDefecto({
      grupos: grupos.map(g => ({
        id: g.id,
        hidden: !!g.hidden,
        is_income: !!g.is_income,
        categories: (g.categories ?? []).map(c => ({
          id: c.id,
          hidden: !!c.hidden,
          is_income: !!c.is_income,
          plantillas: tiposPlantilla(c),
        })),
      })),
      grupoDia: leerTravesia(rawTravesia)?.ids?.grupos?.dia ?? null,
      gruposAnuales: new Set(anuales),
    });
  }, [guardadas, categorias, grupos, rawTravesia, anuales]);

  const guardarIds = useCallback(
    (nuevos: string[]) => setRaw(JSON.stringify(nuevos)),
    [setRaw],
  );

  const elegidasEntidades = useMemo(
    () => categorias.filter(c => ids.includes(c.id)),
    [categorias, ids],
  );
  const datos = useDatosObjetivos(month, elegidasEntidades);

  const hoyFecha = monthUtils.currentDay();
  const { data: deHoy } = useQuery<{ amount: number }>(
    () =>
      ids.length === 0
        ? null
        : q('transactions')
            .filter({ date: hoyFecha, category: { $oneof: ids } })
            .options({ splits: 'inline' })
            .select(['amount']),
    [hoyFecha, ids],
  );

  const hoy = useMemo(() => {
    if (ids.length > 0 && (deHoy == null || datos.size < ids.length)) {
      return null;
    }
    const disponible = ids.reduce(
      (s, id) => s + (datos.get(id)?.balance ?? 0),
      0,
    );
    const gastadoHoy = -(deHoy ?? []).reduce((s, t) => s + t.amount, 0);
    const [, , dia] = hoyFecha.split('-').map(Number);
    return calcularHoy({
      disponible,
      gastadoHoy,
      dia,
      diasMes: Number(monthUtils.getMonthEnd(hoyFecha).slice(8, 10)),
    });
  }, [ids, deHoy, datos, hoyFecha]);

  return {
    hoy,
    ids,
    elegidas: guardadas != null,
    guardarIds,
    grupos,
    categorias,
  };
}

/** Gastos de los últimos días, para «Repetir» y las categorías más usadas. */
export function useGastosRecientes(): GastoReciente[] {
  const desde = monthUtils.subDays(monthUtils.currentDay(), DIAS_RECIENTES);
  const { data } = useQuery<GastoReciente>(
    () =>
      q('transactions')
        .filter({
          date: { $gte: desde },
          amount: { $lt: 0 },
          category: { $ne: null },
          'account.offbudget': false,
        })
        .options({ splits: 'inline' })
        .select(['date', 'amount', 'category', 'payee', 'account']),
    [desde],
  );
  return useMemo(() => [...(data ?? [])], [data]);
}
